import React, { useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { Icon } from "@iconify/react";
import { useCashback, fmtBaht, dayParts, expiryText, todayText, turnPct, whenText, STATUS_LABEL } from "../cashbackLogic";
import { Empty, LoginGate, Skeleton } from "../ui";

/*
 * หน้า "คืนยอดเสีย" — ดีไซน์ landing-ver2 (แดชบอร์ดนีออน): เกจครึ่งวงกลมแสดงยอดเสียวันนี้ · แถบ 7 วัน · ชิปกติกา
 * (ตรรกะอยู่ที่ cashbackLogic.js ร่วมกับ landing ซึ่งใช้ดีไซน์ร่มรับเงินคืนแทน)
 */

function Done({ result, onClose }) {
  const closeRef = useRef(null);
  useEffect(() => {
    if (!result) return undefined;
    if (closeRef.current) closeRef.current.focus();
    const onKey = (e) => e.key === "Escape" && onClose();
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [result, onClose]);
  if (!result) return null;
  const ok = result.ok;
  return (
    <div className="cbn-overlay" role="dialog" aria-modal="true" aria-label={ok ? "รับคืนยอดเสียสำเร็จ" : "รับคืนยอดเสียไม่สำเร็จ"} onClick={onClose}>
      <div className="cbn-done" onClick={(e) => e.stopPropagation()}>
        {ok ? (
          <>
            <Icon icon="fluent-emoji-flat:money-with-wings" width={88} height={88} className="cbn-done-ic" />
            <div className="cbn-done-kicker">
              <Icon icon="solar:check-circle-bold" width={16} /> CASHBACK RECEIVED
            </div>
            <div className="cbn-done-amt">+{fmtBaht(result.amount)}</div>
            <p>รวม {result.count} รายการ เข้ากระเป๋าของคุณแล้ว</p>
            <ul>
              {result.turnover_required > 0 && (
                <li>
                  <Icon icon="solar:lock-keyhole-minimalistic-bold" width={16} /> ต้องแทงให้ครบ {fmtBaht(result.turnover_required)} ก่อนถอนโบนัสส่วนนี้
                </li>
              )}
              {result.play_label && (
                <li>
                  <Icon icon="solar:ticket-bold" width={16} /> โบนัสนี้แทงได้{result.play_label}
                </li>
              )}
            </ul>
          </>
        ) : (
          <>
            <Icon icon="fluent-emoji-flat:umbrella-with-rain-drops" width={72} height={72} />
            <div className="cbn-done-kicker err">รับไม่สำเร็จ</div>
            <p>{result.message}</p>
          </>
        )}
        <button type="button" ref={closeRef} className="cbn-btn" onClick={onClose}>
          ตกลง
        </button>
      </div>
    </div>
  );
}

export default function CashbackPage({ onDone }) {
  const nav = useNavigate();
  const cb = useCashback(onDone);
  const { view } = cb;

  if (!cb.isAuthenticated) {
    return (
      <LoginGate
        icon="solar:chart-2-bold-duotone"
        title="เข้าสู่ระบบเพื่อรับคืนยอดเสีย"
        desc="เสียแล้วยังได้คืน — ระบบคำนวณยอดเสียสุทธิรายวันให้อัตโนมัติ แล้วกดรับได้เลย"
        perks={["คำนวณให้อัตโนมัติทุกวัน", "ดูยอดเสียและยอดที่จะได้คืน", "ดูประวัติการรับ"]}
        onLogin={() => nav("/auth")}
      />
    );
  }
  if (cb.loading) return <Skeleton h={320} style={{ marginTop: 18 }} />;

  const s = view.setting;
  const today = view.today;
  const target = s && s.min_loss > 0 ? s.min_loss : 0;
  const gaugePct = today ? (target > 0 ? Math.min(100, (today.net_loss / target) * 100) : today.net_loss > 0 ? 100 : 0) : 0;
  const canClaim = cb.enabled && view.pending.length > 0;
  const chips = s
    ? [
        { icon: "solar:hand-money-bold-duotone", text: `คืน ${s.percent}%` },
        s.min_loss > 0 && { icon: "solar:arrow-down-bold-duotone", text: `เสียขั้นต่ำ ${fmtBaht(s.min_loss)}` },
        s.max_cashback > 0 && { icon: "solar:arrow-up-bold-duotone", text: `สูงสุด ${fmtBaht(s.max_cashback)}/วัน` },
        { icon: "solar:clock-circle-bold-duotone", text: `กดรับใน ${s.claim_days} วัน` },
        s.turnover_multiplier > 0 && { icon: "solar:lock-keyhole-minimalistic-bold-duotone", text: `เทิร์น ${s.turnover_multiplier} เท่า` },
        s.play_scope !== "ALL" && { icon: "solar:ticket-bold-duotone", text: `โบนัสแทงได้${s.play_label}` },
        s.calc_scope !== "ALL" && { icon: "solar:chart-2-bold-duotone", text: `นับยอดเสียจาก${String(s.calc_label).replace(/^เฉพาะ /, "")}` },
      ].filter(Boolean)
    : [];

  return (
    <div className="cbn-page">
      {/* ---- เกจนีออน ---- */}
      <section className={`cbn-hero${cb.enabled ? "" : " is-off"}`}>
        <div className="cbn-glow" aria-hidden="true" />
        <div className="cbn-head">
          <Icon icon="fluent-emoji-flat:money-with-wings" width={40} height={40} />
          <div>
            <h1>คืนยอดเสีย</h1>
            <p>เสียสุทธิรายวัน → คืนให้อัตโนมัติ</p>
          </div>
          <span className={`cbn-led ${cb.enabled ? "on" : "off"}`}>{cb.enabled ? "ACTIVE" : "ปิดอยู่"}</span>
        </div>

        {cb.enabled ? (
          <>
            <div className="cbn-gauge" role="img" aria-label={`ยอดเสียวันนี้ ${fmtBaht(today ? today.net_loss : 0)}`}>
              <svg viewBox="0 0 200 110" width="260" height="143">
                <path d="M 20 100 A 80 80 0 0 1 180 100" className="cbn-gauge-bg" pathLength="100" />
                <path d="M 20 100 A 80 80 0 0 1 180 100" className="cbn-gauge-fg" pathLength="100" style={{ strokeDasharray: `${gaugePct} 100` }} />
              </svg>
              <div className="cbn-gauge-in">
                <small>ยอดเสียวันนี้</small>
                <b>{fmtBaht(today ? today.net_loss : 0)}</b>
                <span>{target > 0 ? `ขั้นต่ำ ${fmtBaht(target)}` : "ไม่มีขั้นต่ำ"}</span>
              </div>
            </div>
            <p className="cbn-est">{todayText(today)}</p>
            <div className="cbn-pend-total">
              <div>
                <span>รอรับตอนนี้</span>
                <b>{fmtBaht(view.totals.pending)}</b>
              </div>
              <button type="button" className="cbn-btn sm" disabled={!canClaim || cb.claimingId === "ALL"} onClick={() => cb.claim(null)}>
                <Icon icon={cb.claimingId === "ALL" ? "svg-spinners:ring-resize" : "solar:wallet-money-bold"} width={18} />
                {cb.claimingId === "ALL" ? "กำลังรับ..." : "รับทั้งหมด"}
              </button>
            </div>
          </>
        ) : (
          <div className="cbn-off">
            <Icon icon="solar:lock-keyhole-minimalistic-bold" width={18} /> ระบบคืนยอดเสียปิดให้บริการชั่วคราว
          </div>
        )}
      </section>

      {chips.length > 0 && (
        <div className="cbn-chips" aria-label="เงื่อนไขการคืนยอดเสีย">
          {chips.map((c) => (
            <span key={c.text}>
              <Icon icon={c.icon} width={16} /> {c.text}
            </span>
          ))}
        </div>
      )}

      {/* ---- แถบ 7 วัน ---- */}
      <section className="cbn-sec">
        <h2>
          <Icon icon="solar:calendar-date-bold-duotone" width={20} /> 7 วันล่าสุด
        </h2>
        <ol className="cbn-week">
          {view.week.map((w) => {
            const d = dayParts(w.date);
            return (
              <li key={w.date} className={`st-${String(w.status).toLowerCase()}`}>
                <small>{d.dow}</small>
                <b>{d.day}</b>
                <span className="cbn-week-amt">{w.amount > 0 ? fmtBaht(w.amount) : "-"}</span>
                <em>{STATUS_LABEL[w.status] || w.status}</em>
              </li>
            );
          })}
        </ol>
      </section>

      {/* ---- รายการรอรับ ---- */}
      {cb.enabled && (
        <section className="cbn-sec">
          <h2>
            <Icon icon="solar:wallet-money-bold-duotone" width={20} /> รอกดรับ <b>{view.pending.length}</b>
          </h2>
          {view.pending.length === 0 ? (
            <div className="lx-card">
              <Empty icon="solar:chart-2-bold-duotone">ยังไม่มียอดคืนที่รอรับ — ระบบคำนวณทุกวัน 01:03 น.</Empty>
            </div>
          ) : (
            <div className="cbn-pend">
              {view.pending.map((p) => {
                const ex = expiryText(p);
                return (
                  <article className="cbn-card" key={p.id}>
                    <div className="cbn-card-date">
                      <b>{dayParts(p.date).day}</b>
                      <span>{dayParts(p.date).month}</span>
                    </div>
                    <div className="cbn-card-body">
                      <strong>{fmtBaht(p.amount)}</strong>
                      <span>
                        เสียสุทธิ {fmtBaht(p.net_loss)} · คืน {p.percent}%
                      </span>
                      {ex.text && <em className={ex.urgent ? "urgent" : ""}>{ex.text}</em>}
                    </div>
                    <button type="button" className="cbn-btn sm" disabled={cb.claimingId === p.id || cb.claimingId === "ALL"} onClick={() => cb.claim(p.id)}>
                      {cb.claimingId === p.id ? "กำลังรับ..." : "รับ"}
                    </button>
                  </article>
                );
              })}
            </div>
          )}
        </section>
      )}

      {view.active.length > 0 && (
        <section className="cbn-sec">
          <h2>
            <Icon icon="solar:shield-keyhole-bold-duotone" width={20} /> โบนัสที่ยังมีเงื่อนไข
          </h2>
          <div className="cbn-actives">
            {view.active.map((a) => (
              <article className="cbn-active" key={a.id}>
                <div className="cbn-meter" style={{ "--p": `${a.locked ? turnPct(a) : 100}%` }} aria-hidden="true">
                  <b>{a.locked ? `${Math.round(turnPct(a))}%` : <Icon icon="solar:ticket-bold" width={18} />}</b>
                </div>
                <div className="cbn-active-body">
                  <div>
                    <code>{dayParts(a.date).label}</code>
                    <strong>{fmtBaht(a.amount)}</strong>
                  </div>
                  {a.locked && (
                    <span>
                      ทำเทิร์นแล้ว {fmtBaht(a.turnover_done)} จาก {fmtBaht(a.turnover_required)}
                    </span>
                  )}
                  {a.restricted_left > 0 && (
                    <span>
                      เหลือ {fmtBaht(a.restricted_left)} · {a.play_label}
                    </span>
                  )}
                </div>
              </article>
            ))}
          </div>
        </section>
      )}

      <section className="cbn-sec">
        <h2>
          <Icon icon="solar:history-bold-duotone" width={20} /> ประวัติการรับ {view.totals.received > 0 && <small>รวม {fmtBaht(view.totals.received)}</small>}
        </h2>
        {view.history.length === 0 ? (
          <div className="lx-card">
            <Empty icon="solar:chart-2-bold-duotone">ยังไม่เคยรับคืนยอดเสีย</Empty>
          </div>
        ) : (
          <ol className="cbn-timeline">
            {view.history.map((h) => (
              <li key={h.id} className={h.status === "RECEIVED" ? "ok" : "off"}>
                <span className="cbn-dot">
                  <Icon icon={h.status === "RECEIVED" ? "solar:check-read-bold" : "solar:close-circle-bold"} width={14} />
                </span>
                <div className="cbn-tl-body">
                  <b>{h.status === "RECEIVED" ? "รับแล้ว" : "หมดสิทธิ์"} · {dayParts(h.date).label}</b>
                  <span>
                    เสียสุทธิ {fmtBaht(h.net_loss)} · {h.status === "RECEIVED" ? whenText(h.received_at) : "เกินกำหนดกดรับ"}
                  </span>
                </div>
                <strong>{fmtBaht(h.amount)}</strong>
              </li>
            ))}
          </ol>
        )}
      </section>

      {view.rules.length > 0 && (
        <section className="cbn-sec">
          <h2>
            <Icon icon="solar:document-text-bold-duotone" width={20} /> กติกา
          </h2>
          <ul className="cbn-rules">
            {view.rules.map((r) => (
              <li key={r}>{r}</li>
            ))}
            {s && s.detail && <li>{s.detail}</li>}
          </ul>
        </section>
      )}

      <Done result={cb.result} onClose={cb.closeResult} />
    </div>
  );
}
